import "../types/index.js";

import { subscriptions } from "../events/subscriptions.js";
import { services } from "../services/services.js";
import {
  getAttributeValue,
  parseAttributeValue,
  setAttributeValue,
} from "./attributes.js";
import { getInheritableStaticArrayValues } from "./inheritable_statics.js";
import { downcaseFirst, kebabToCamelCase, upcaseFirst } from "./strings.js";

/**
 * Read service dependencies from static services array on component and
 * define accessors. See docs in DickTokComponent.
 * @param {DickTokComponent} root
 */
export function bindServices(root) {
  bindSingletonFromMap(root, "services", services);
}

/**
 * Read subscription dependencies from static services array on component and
 * define accessors. See docs in DickTokComponent.
 * @param {DickTokComponent} root
 */
export function bindSubscriptions(root) {
  bindSingletonFromMap(root, "subscriptions", subscriptions);
}

/**
 * @param {DickTokComponent} root
 * @param {string} property
 * @param {Map<string, ?>} map
 */
export function bindSingletonFromMap(root, property, map) {
  root[property] = new Map(
    getInheritableStaticArrayValues(root.constructor, property).map((name) => {
      const bindable = map.get(name);
      if (!bindable) {
        throw new Error(
          `Undefined ${name} required by ${root.tagName.toLowerCase()}`,
        );
      }
      return [name, bindable];
    }),
  );
  for (const [name, bindable] of root[property]) {
    Object.defineProperty(root, downcaseFirst(name), { value: bindable });
  }
}

/**
 * Read target configuration from static targets array on component and
 * define accessors. See docs in DickTokComponent.
 * @param {DickTokComponent} root
 */
export function bindTargets(root) {
  for (const targetName of root.constructor.targets) {
    Object.defineProperty(root, `${targetName}Target`, {
      get() {
        return this.getElement(`[data-target="${targetName}"]`);
      },
    });
    Object.defineProperty(root, `${targetName}Targets`, {
      get() {
        return this.getElements(`[data-target="${targetName}"]`);
      },
    });
    Object.defineProperty(root, `has${upcaseFirst(targetName)}Target`, {
      get() {
        return (
          this.getOptionalElement(`[data-target="${targetName}"]`) !== null
        );
      },
    });
  }
}

/**
 * Read attribute configuration from static attributes array on component and
 * define accessors and callbacks. See docs in DickTokComponent.
 * @param {DickTokComponent} root
 */
export function bindAttributes(root) {
  // Don't set this as root.attributes because that shadows Element.attributes.
  // We also don't need to provide this as an API service right now as no
  // component currently needs access to the attribute configuration.
  const attributes = new Map(
    root.constructor.attributes.map(({ name, type, optional = false }) => [
      name,
      {
        attributeName: name,
        propertyName: kebabToCamelCase(name),
        type,
        optional,
      },
    ]),
  );
  for (const attribute of attributes.values()) {
    Object.defineProperty(root, attribute.propertyName, {
      get() {
        return getAttributeValue(root, attribute);
      },
      set(value) {
        return setAttributeValue(root, attribute, value);
      },
    });
    // For optional attributes, define `hasFoo` getter
    if (attribute.optional) {
      Object.defineProperty(root, `has${upcaseFirst(attribute.propertyName)}`, {
        get() {
          return this.hasAttribute(attribute.attributeName);
        },
      });
    }
  }
  // Call attribute change callbacks if defined
  bindAttributeChangedCallbacks(root, attributes);
}

/**
 * @param {DickTokComponent} root
 * @param {Map<string, AttributeBinding>} attributes
 */
function bindAttributeChangedCallbacks(root, attributes) {
  const attributesToWatch = [...attributes.values()]
    .filter(
      ({ propertyName }) =>
        typeof root[`${propertyName}Changed`] === "function",
    )
    .map(({ attributeName }) => attributeName);
  if (attributesToWatch.length === 0) {
    return;
  }
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      const attribute = attributes.get(mutation.attributeName);
      root[`${attribute.propertyName}Changed`](
        parseAttributeValue(attribute, mutation.oldValue),
        root[attribute.propertyName],
      );
    }
  });
  observer.observe(root, {
    attributeFilter: attributesToWatch,
    attributeOldValue: true,
  });
  root.disconnectSignal.addEventListener("abort", () => {
    observer.disconnect();
  });
}

/**
 * Find child elements of root that have a `data-action` attribute and bind
 * event handlers. See docs for `bindActionsFromElements()`.
 * @param {DickTokComponent} root DickTokComponent
 */
export function bindActions(root) {
  // If shadow DOM is attached, getElements looks in shadow DOM, otherwise
  // getElements looks in light DOM.
  bindActionsFromElements(root, root.getElements("[data-action]"));
}

/**
 * Discover elements with `data-action` attribute and bind event handlers.
 *
 * The syntax for the value is `"event:selector->method"`. For example:
 * ```html
 * <button data-action="click:#counter->incrementFoo">
 *   Increment foo
 * </button>
 * ```
 * When the component is initialized, this method is called to discover
 * elements with actions and add event listeners is added that will call the
 * `incrementFoo(event)` method on the closest element matching `#counter`
 * when the button is clicked. (Only the light DOM is searched for selectors,
 * but this includes the component itself, for example, an element inside of a
 * dt-sheet can find the DickTokSheet component to call a method on it, as the
 * host element is in the light DOM.)
 *
 * The event can be any native event, for example `click`, `keyup`, etc.
 *
 * Multiple handlers can also be attached to the same element by separating
 * them by a space. For example:
 * ```
 * <button data-action="click:el->foo touchend:el->bar">
 *   Click me
 * </button>
 * ```
 *
 * @param {DickTokComponent} root
 * @param {Array<HTMLElement>} elements
 */
function bindActionsFromElements(root, elements) {
  for (const element of elements) {
    // The action may be discovered multiple times, since elements in the light
    // DOM are visible to ancestors, even if the element is nearer to a child
    // component. (In fact, this function will be called first on components at
    // the top of the DOM tree because connectedCallback() is called when the
    // opening tag of a component is parsed.) We could try to avoid this, but
    // this workaround is fine for now.
    if (element.dataset.actionBound !== undefined) {
      continue;
    }
    element.dataset.actionBound = "";
    // Parse actions and attach event listeners
    const actions = element.dataset.action.split(" ");
    for (const action of actions) {
      const [event, descriptor] = action.split(":");
      const [targetSelector, method] = descriptor.split("->");
      element.addEventListener(
        event,
        function (event) {
          const inShadowDom = element.getRootNode() instanceof DocumentFragment;
          // Even if the element is in the shadow DOM, the handler would be
          // defined on the component itself or one of it's ancestors, so we need
          // to break out of the shadow DOM to find the target where the handler
          // method is defined.
          const target = inShadowDom
            ? element.getRootNode().host.closest(targetSelector)
            : element.closest(targetSelector);
          const boundMethod = target[method].bind(target);
          boundMethod(event);
        },
        { signal: root.disconnectSignal },
      );
    }
  }
}

/**
 * Watch child elements for changes in order to bind actions when new child
 * elements are added to a component and enable `childrenChangedCallback()`.
 * @param {DickTokComponent} root
 */
export function bindActionsLateLoaded(root) {
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Bind actions when new child elements are added
      bindActionsFromElements(
        root,
        [...mutation.addedNodes]
          .filter((node) => node.nodeType === Node.ELEMENT_NODE)
          .flatMap((element) => [element, ...element.querySelectorAll("*")])
          .filter((element) => element.matches("[data-action]")),
      );
      root.childrenChangedCallback(mutation);
    }
  });
  // If shadow DOM is attached, watch shadow DOM, otherwise watch light DOM.
  // This mirrors the behavior of bindActions() which relies on the same
  // behavior in getElements().
  observer.observe(root.shadowRoot ? root.shadowRoot : root, {
    childList: true,
    subtree: true,
  });
  root.disconnectSignal.addEventListener("abort", () => {
    observer.disconnect();
  });
}
